import { useReactiveVar } from '@apollo/client';
import { NavigationProp, useFocusEffect } from '@react-navigation/core';
import { useNavigation, useRoute } from '@react-navigation/native';
import { theme } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import ParkDetailButtons from 'components/park/detail/ParkDetailButtons';
import { CardItem } from 'components/services';
import SvgImage from 'components/svgImage/SvgImage';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import AccountContext from 'context/AccountContext';
import ParkingContext from 'context/ParkingContext';
import ParkingDetailSkeletonLoader from 'context/ParkingDetailSkeletonLoader';
import useDynamicSheetSize from 'hooks/useDynamicSheetSize';
import useParkingPricingInfo from 'hooks/useParkingPricingInfo';
import useParkingZoneChangeCountry from 'hooks/useParkingZoneChangeCountry';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { featureCountryCode } from 'states/persistInStorage';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useParkingStore } from 'stores/parkingStore';
import { usePermitStore } from 'stores/permitStore';
import styled from 'styled-components/native';
import { CountryCode } from 'types/generatedSchemaTypes';
import { ParkNavProps } from 'types/navigation';
import {
  parseFeatureFlagObject,
  showFeatureFlag,
  TypeDictionary,
} from 'utils/featureFlagUtils';
import {
  findStaffMemberBenefit,
  getStaffMemberAdmission,
  StaffMemberAdmissionType,
} from 'utils/memberGroupAdmissionUtils';
import {
  addToRecentZonesOrChargers,
  removeFromRecentZonesOrChargers,
} from 'utils/parkUtils';
import ParkingZoneParkTab from './tabs/ParkingZoneParkTab';
import { useUiStore } from 'stores/uiStore';

export const Wrapper = styled.View`
  padding: 20px 20px 37px;
  background-color: ${theme.colors.white};
  gap: 11px;
`;

type RouteParams = {
  parkingZoneUid?: string;
};

export const ParkingZoneDetails: React.FC = () => {
  const { parkingZoneResult: parkingZone, isLoadingParkZoneDetails } =
    useContext(ParkingContext);
  const { memberGroupUids } = useContext(AccountContext);
  const benefits = parkingZone?.data?.parkZone?.benefits ?? [];

  const { setParkingConditions, parkingConditions } = useParkingStore(
    (state) => state
  );

  const { parkingMethods } = parkingZone?.data?.parkZone || {};
  const isUnmanagedParking = parkingMethods?.unmanagedParking === true;
  const navigation: NavigationProp<any> = useNavigation<ParkNavProps>();
  const { t } = useTranslation();
  useParkingZoneChangeCountry();
  const { handleLayout } = useDynamicSheetSize();

  const {
    flags: {
      stp_tariff,
      enable_healthcare_permit_button,
      enable_unlock_mg_automatic_pmc_button,
    },
  } = useContext(FeatureFlagsContext);
  const showSTPTariff = showFeatureFlag(stp_tariff);
  const appCountry = useReactiveVar(featureCountryCode) as CountryCode;
  const healthcareCountryObject = parseFeatureFlagObject<
    TypeDictionary<CountryCode, boolean>
  >(enable_healthcare_permit_button, 'enable_healthcare_permit_button');
  const unlockAutomaticPmcCountryObject = parseFeatureFlagObject<
    TypeDictionary<CountryCode, boolean>
  >(
    enable_unlock_mg_automatic_pmc_button,
    'enable_unlock_mg_automatic_pmc_button'
  );

  const showHealthcarePermitButton = Object.keys(healthcareCountryObject)
    .filter((key) => healthcareCountryObject[key as CountryCode])
    .map((key) => key as CountryCode);

  const showUnlockAutomaticPmcButton = Object.keys(
    unlockAutomaticPmcCountryObject
  )
    .filter((key) => unlockAutomaticPmcCountryObject[key as CountryCode])
    .map((key) => key as CountryCode);

  const enabledStaffMemberAdmissions = useMemo(() => {
    const admissions: StaffMemberAdmissionType[] = [];
    if (showUnlockAutomaticPmcButton.includes(appCountry)) {
      admissions.push('UNLOCK_CODE_MATCH');
    }
    if (showHealthcarePermitButton.includes(appCountry)) {
      admissions.push('HSA_EMPLOYEE_UNIT_MATCH');
    }
    return admissions;
  }, [appCountry, showHealthcarePermitButton, showUnlockAutomaticPmcButton]);

  const staffMemberBenefit = useMemo(
    () =>
      findStaffMemberBenefit(
        benefits,
        memberGroupUids,
        enabledStaffMemberAdmissions
      ),
    [benefits, memberGroupUids, enabledStaffMemberAdmissions]
  );
  const staffMemberAdmission = staffMemberBenefit
    ? getStaffMemberAdmission(staffMemberBenefit, enabledStaffMemberAdmissions)
    : undefined;

  const showStaffMemberCard = !!staffMemberBenefit && !!staffMemberAdmission;

  const showEVChargingPublicUnmanagedParking =
    !!parkingMethods?.unmanagedParking;

  const route = useRoute();

  const { pricingSchemeId } = useParkingPricingInfo(
    parkingZone?.data?.parkZone?.uid ?? '',
    true,
    isUnmanagedParking
  );

  useFocusEffect(
    React.useCallback(() => {
      const routeParams: RouteParams = route?.params || {};
      const routeParamsParkingZoneUid = routeParams?.parkingZoneUid;
      const currentParkingZoneUid =
        parkingConditions.selectedParkingZoneId ??
        useActiveElementStore.getState().activeElement;
      if (routeParamsParkingZoneUid !== currentParkingZoneUid) {
        // '' is the store's "nothing selected" value. The reactive var used to
        // take undefined here because its setter parameter was optional.
        useActiveElementStore
          .getState()
          .setActiveElement(routeParamsParkingZoneUid ?? '');
        setParkingConditions({
          selectedParkingZoneId: routeParamsParkingZoneUid,
        });
        // Entering a different zone: clear the manual-tariff guard so the new
        // zone gets its normal pre-selection (see useTariffs).
        useParkingStore.getState().setHasManuallySelectedTariff(false);
      }
    }, [route, parkingConditions.selectedParkingZoneId, setParkingConditions])
  );

  useEffect(() => {
    if (
      parkingZone?.data?.parkZone &&
      parkingZone.data.parkZone.serviceChannelCodes &&
      process.env.SERVICE_CHANNEL_CODE
    ) {
      if (
        parkingZone.data.parkZone.serviceChannelCodes?.includes(
          process.env.SERVICE_CHANNEL_CODE
        ) &&
        parkingZone.data.parkZone.commerciallyActive
      ) {
        addToRecentZonesOrChargers(parkingZone?.data?.parkZone);
      } else {
        showToast(t('parking.parkzoneNotAvailable'), 'info');
        removeFromRecentZonesOrChargers(parkingZone?.data?.parkZone);
        navigation.navigate('park');
      }
    }
  }, [
    isUnmanagedParking,
    navigation,
    parkingZone?.data?.parkZone,
    showEVChargingPublicUnmanagedParking,
    showSTPTariff,
    t,
  ]);

  const handleStaffMemberPress = useCallback(() => {
    if (!staffMemberBenefit || !staffMemberAdmission) {
      return;
    }
    if (staffMemberAdmission === 'UNLOCK_CODE_MATCH') {
      usePermitStore.getState().setSelectedParkZoneBenefit(staffMemberBenefit);
      useUiStore.getState().setSheetModal('unlockMemberGroup');
      return;
    }
    useUiStore.getState().setSheetModal('PermitHsaModal');
  }, [staffMemberAdmission, staffMemberBenefit]);

  return (
    <Wrapper onLayout={(e) => handleLayout(e.nativeEvent.layout)}>
      {isLoadingParkZoneDetails ? (
        <ParkingDetailSkeletonLoader />
      ) : (
        <>
          <ParkingZoneParkTab parkZone={parkingZone?.data?.parkZone} />
          {showStaffMemberCard && (
            <CardItem
              title={t('longTermParking.staffMemberPermit.bannerTitle')}
              text={t('longTermParking.staffMemberPermit.bannerDescription')}
              testID="staff-member-permit-chip"
              onPress={handleStaffMemberPress}
              countryToShow={[appCountry]}
              bgColor="#C9AAD1"
              leftImageContainer={
                <SvgImage size="xxl" name={'Permit'} testID={'permit-icon'} />
              }
            />
          )}
          <ParkDetailButtons parkingWithApp={!!pricingSchemeId} />
        </>
      )}
    </Wrapper>
  );
};

export default React.memo(ParkingZoneDetails);
